import type { BaseQueryApi, BaseQueryFn } from "@reduxjs/toolkit/query"
import { Mutex } from "async-mutex"
import { App } from "@app/interfaces"
import { keycloakTokenService } from "@app/services/oidc/keycloakTokenService"
import { setAuth, resetAuth, AuthState } from "."
import { RootState, StateFromRoot } from "@/core/store"

const mutex = new Mutex()

const getAuthState = (api: BaseQueryApi): AuthState => {
  const state = api.getState() as RootState<StateFromRoot<AuthState>>
  return state.auth
}

const calculateExpiry = (expiresIn: number) =>
  Math.floor(Date.now() / 1000) + expiresIn

const collectRoles = (profile?: Partial<App.OIDCUserInfo>): string[] => {
  if (!profile) return []
  const inline = profile.roles ?? []
  const realm = profile.realm_access?.roles ?? []
  const resource = profile.resource_access
    ? Object.values(profile.resource_access).flatMap(
        (value) => value.roles ?? [],
      )
    : []
  return [...inline, ...realm, ...resource].map((role) => role.toLowerCase())
}

const deriveRole = (
  profile?: Partial<App.OIDCUserInfo>,
  fallback: App.UserRole = App.UserRole.USER,
): App.UserRole => {
  const roles = collectRoles(profile)
  if (roles.includes("admin")) return App.UserRole.ADMIN
  if (roles.includes("manager") || roles.includes("operator"))
    return App.UserRole.MANAGER
  if (roles.includes("tgmanager") || roles.includes("user"))
    return App.UserRole.USER
  return fallback
}

const ensureOIDCUserInfo = (
  current: App.OIDCUserInfo | undefined,
  profile?: Partial<App.OIDCUserInfo>,
): App.OIDCUserInfo => {
  const base: App.OIDCUserInfo = current ?? {
    sub: "",
    preferred_username: "",
  }
  return {
    ...base,
    ...profile,
    sub: profile?.sub ?? base.sub ?? "",
    preferred_username:
      profile?.preferred_username ?? base.preferred_username ?? "",
    email: profile?.email ?? base.email,
    given_name: profile?.given_name ?? base.given_name,
    family_name: profile?.family_name ?? base.family_name,
    roles: profile?.roles ?? base.roles,
    realm_access: profile?.realm_access ?? base.realm_access,
    resource_access: profile?.resource_access ?? base.resource_access,
  }
}

const shouldRefreshNow = (auth: AuthState, force: boolean) =>
  force || keycloakTokenService.shouldRefreshToken(auth.expiresAt)

const refreshTokens = async (
  api: BaseQueryApi,
  force = false,
  isCurrent: () => boolean = () => true,
): Promise<boolean> => {
  if (!isCurrent()) return false
  const initial = getAuthState(api)

  if (initial.type !== App.AuthType.OIDC || !initial.refreshToken) {
    return false
  }

  if (!keycloakTokenService.isConfigured()) {
    return false
  }

  if (!shouldRefreshNow(initial, force)) {
    return true
  }

  if (mutex.isLocked()) {
    await mutex.waitForUnlock()
    return isCurrent()
  }

  const release = await mutex.acquire()
  try {
    if (!isCurrent()) return false
    const auth = getAuthState(api)

    if (auth.type !== App.AuthType.OIDC || !auth.refreshToken) {
      api.dispatch(resetAuth())
      return false
    }

    if (!shouldRefreshNow(auth, force)) {
      return true
    }

    const refreshed = await keycloakTokenService.refreshToken(auth.refreshToken)
    // A refresh started for another session must not replace the current auth.
    if (!isCurrent()) return false

    // Извлекаем роли из нового access_token (как в OIDCCallback)
    let roleFromToken: string[] = []
    try {
      const tokenPayload = JSON.parse(
        atob(refreshed.access_token.split(".")[1]),
      )

      if (tokenPayload.roles && Array.isArray(tokenPayload.roles)) {
        roleFromToken = tokenPayload.roles
      } else if (
        tokenPayload.realm_access?.roles &&
        Array.isArray(tokenPayload.realm_access.roles)
      ) {
        roleFromToken = tokenPayload.realm_access.roles
      } else if (
        tokenPayload.resource_access &&
        typeof tokenPayload.resource_access === "object"
      ) {
        for (const [resource, access] of Object.entries(
          tokenPayload.resource_access,
        )) {
          if (
            access &&
            typeof access === "object" &&
            "roles" in access &&
            Array.isArray(access.roles)
          ) {
            roleFromToken = [...roleFromToken, ...access.roles]
          }
        }
      }
    } catch (decodeError) {
      console.error("Failed to decode refreshed token for roles:", decodeError)
      // Fallback к старым ролям
      roleFromToken = (auth.userInfo as App.OIDCUserInfo)?.roles || []
    }

    const profile = refreshed.id_token
      ? keycloakTokenService.parseUserInfo(refreshed.id_token)
      : (auth.userInfo as App.OIDCUserInfo | undefined)

    // Определяем роль пользователя на основе ролей из токена
    let userRole = App.UserRole.USER
    if (roleFromToken.includes("admin")) {
      userRole = App.UserRole.ADMIN
    } else if (
      roleFromToken.includes("manager") ||
      roleFromToken.includes("Operator")
    ) {
      userRole = App.UserRole.MANAGER
    }

    const updated: AuthState = {
      ...auth,
      type: App.AuthType.OIDC,
      token: refreshed.access_token,
      refreshToken: refreshed.refresh_token ?? auth.refreshToken,
      idToken: refreshed.id_token ?? auth.idToken,
      expiresAt: calculateExpiry(refreshed.expires_in),
      userInfo: {
        ...(auth.userInfo as App.OIDCUserInfo),
        ...profile,
        roles: roleFromToken, // Используем роли из нового access_token
      },
      role: userRole,
    }

    api.dispatch(setAuth(updated))
    return true
  } catch (error) {
    // Nor may a failed stale refresh sign out the newly authenticated account.
    if (!isCurrent()) return false
    console.error("OIDC token refresh failed", error)
    api.dispatch(resetAuth())
    return false
  } finally {
    release()
  }
}

const shouldRetryUnauthorized = (result: unknown): boolean => {
  const error = (
    result as { error?: { status?: number; originalStatus?: number } }
  )?.error
  return error?.status === 401 || error?.originalStatus === 401
}

interface ReauthOptions {
  /** Opt-in operation fence. Identity must stay stable across token refreshes.
   * Existing callers retain their auth behavior unless they supply this key. */
  getSessionIdentity?: (auth: AuthState) => unknown
}

const sessionChanged = () => ({
  error: {
    status: "CUSTOM_ERROR" as const,
    error: "Request cancelled: authentication session changed",
  },
})

export const baseQueryWithReauth = (
  baseQuery: BaseQueryFn,
  { getSessionIdentity }: ReauthOptions = {},
): BaseQueryFn => {
  return async (args, api, extraOptions) => {
    // Capture before waitForUnlock, not at the first HTTP call: a queued
    // operation still belongs to the account that originally initiated it.
    const identity = getSessionIdentity?.(getAuthState(api))
    const isCurrent = () =>
      !getSessionIdentity || getSessionIdentity(getAuthState(api)) === identity
    await mutex.waitForUnlock()
    if (!isCurrent()) return sessionChanged()
    await refreshTokens(api, false, isCurrent)
    if (!isCurrent()) return sessionChanged()

    let result = await baseQuery(args, api, extraOptions)
    // Discard even successful stale responses, and never refresh B for A's 401.
    if (!isCurrent()) return sessionChanged()

    if (shouldRetryUnauthorized(result)) {
      const refreshed = await refreshTokens(api, true, isCurrent)
      if (!isCurrent()) return sessionChanged()
      if (refreshed) {
        result = await baseQuery(args, api, extraOptions)
        if (!isCurrent()) return sessionChanged()
      }
    }

    return result
  }
}
